import { Logger } from '@frontend/logger';
import React, { ReactNode, createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import ActiveModalContext, { AsyncCleanupFn } from '../active-modal-context/active-modal-context';
import { DefaultModal, DefaultModalProps } from '../default-modal/default-modal.component';

interface ModalInstance {
    id: string;
    render: () => React.ReactNode;
    wrapperOptions?: { handleCloseCallback?: () => Promise<void> | void; backdropDismissible?: boolean };
    modalOptions?: Omit<DefaultModalProps, 'show' | 'handleClose'>;
    cleanupCallback?: () => Promise<boolean | void> | boolean | void;
}

export type OpenModalFunction = (
    id: string,
    render: () => React.ReactNode,
    wrapperOptions?: { handleCloseCallback?: () => void; backdropDismissible?: boolean },
    modalOptions?: Omit<DefaultModalProps, 'show' | 'handleClose'>
) => string;

interface ModalContextType {
    openModal: OpenModalFunction;
    closeModal: (id: string) => void;
    closeTopModal: () => Promise<void>;
    closeAllModals: () => void;
    updateModal: (id: string, newRender: () => React.ReactNode) => void;
    modalStackLength: number;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useModals = () => {
    const context = useContext(ModalContext);
    if (!context) {
        throw new Error('useModals must be used within a ModalProvider');
    }
    return context;
};

interface ModalProviderProps {
    children: ReactNode;
}

export const ModalProvider = (props: ModalProviderProps) => {
    const [modals, setModals] = useState<ModalInstance[]>([]);
    const modalStackRef = useRef<ModalInstance[]>([]);
    modalStackRef.current = modals;
    const modalRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const modalRootRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        // First try to find the app-specific modal container
        let modalRoot = document.getElementById('app-content');

        // If not found, try to find or create modal-root
        if (!modalRoot) {
            modalRoot = document.getElementById('modal-root');

            // Only create modal-root if it doesn't exist
            if (!modalRoot) {
                Logger.log('Creating modal-root element');
                modalRoot = document.createElement('div');
                modalRoot.setAttribute('id', 'modal-root');
                document.body.appendChild(modalRoot);
            }
        }

        modalRootRef.current = modalRoot;
    }, []);

    const registerCleanupForModal = useCallback(async (id: string, cleanupFunction: AsyncCleanupFn | undefined) => {
        setModals((prev) => prev.map((modal) => (modal.id === id ? { ...modal, cleanupCallback: cleanupFunction } : modal)));
    }, []);

    const openModal = useCallback(
        (
            id: string,
            render: () => React.ReactNode,
            wrapperOptions?: { handleCloseCallback?: () => void; backdropDismissible?: boolean },
            modalOptions?: Omit<DefaultModalProps, 'show' | 'handleClose'>
        ) => {
            if (!modalRootRef.current) {
                Logger.error('Modal root not found. Ensure ModalProvider is used at the root of your app.');
                return id;
            }
            if (modalStackRef.current.some((modal) => modal.id === id)) {
                Logger.warn(`Modal with id "${id}" already exists. Updating the existing modal.`);
                updateModal(id, render);
                return id;
            }

            setModals((prev) => {
                return [
                    ...prev,
                    {
                        id,
                        render,
                        wrapperOptions: { backdropDismissible: true, ...wrapperOptions },
                        modalOptions
                    }
                ];
            });
            return id;
        },
        [modalStackRef]
    );

    const closeModal = useCallback((id: string) => {
        setModals((prev) => prev.filter((modal) => modal.id !== id));
    }, []);

    const closeTopModal = useCallback(async () => {
        const topModal = modalStackRef.current[modalStackRef.current.length - 1];

        if (topModal) {
            let shouldClose = true;
            if (topModal.cleanupCallback) {
                const result = await topModal.cleanupCallback();
                shouldClose = result ?? true;
            }

            if (topModal.wrapperOptions?.handleCloseCallback) {
                await topModal.wrapperOptions.handleCloseCallback();
            }

            if (shouldClose && topModal.wrapperOptions?.backdropDismissible) {
                // Prefer id-based remove over slice: a concurrent closeModal(top) must not
                // chain into slicing off the next pane underneath.
                setModals((prev) => prev.filter((modal) => modal.id !== topModal.id));
            }
        }
    }, [modalStackRef]);

    const closeAllModals = useCallback(() => {
        setModals([]);
    }, []);

    const updateModal = useCallback((id: string, newRender: () => React.ReactNode) => {
        setModals((prevModals) => prevModals.map((modal) => (modal.id === id ? { ...modal, render: newRender } : modal)));
    }, []);

    useEffect(() => {
        const handleClickOutside = async (event: MouseEvent) => {
            const target = event.target;
            if (!(target instanceof Element)) {
                return;
            }
            // Floating menus (Dropdown) portal outside the pane and may unmount on the same mousedown.
            // Walk the event target tree — do not rely on getElementById (gone after unmount).
            if (target.closest('.ignore-modal-close') || target.closest('#dropdown-menu') || target.closest('[role="menu"]')) {
                return;
            }

            if (modals.length > 0) {
                const topModal = modals[modals.length - 1];
                const topModalRef = modalRefs.current[topModal.id];
                if (topModalRef && topModal.wrapperOptions?.backdropDismissible && !topModalRef.contains(target)) {
                    await closeTopModal();
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [modals, closeTopModal]);

    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && modals.length > 0) {
                const topModal = modals[modals.length - 1];
                if (topModal.wrapperOptions?.backdropDismissible) {
                    closeTopModal();
                }
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [modals, closeTopModal]);

    const contextValue = {
        openModal,
        closeModal,
        closeTopModal,
        closeAllModals,
        updateModal,
        modalStackLength: modals.length
    };

    return (
        <ModalContext.Provider value={contextValue}>
            {props.children}
            {modalRootRef.current &&
                ReactDOM.createPortal(
                    <>
                        {modals.map((modal, index) => (
                            <ActiveModalContext.Provider
                                key={modal.id}
                                value={{ modalId: modal.id, registerCleanup: registerCleanupForModal }}>
                                <DefaultModal
                                    key={modal.id}
                                    id={modal.id}
                                    modalRef={(el) => (modalRefs.current[modal.id] = el)}
                                    zIndex={1000 + index}
                                    show={true}
                                    // ModalProvider owns outside-click / Escape (closeTopModal).
                                    // DefaultModal's capture-phase listener must not also close —
                                    // both firing chains filter+slice and drops the pane underneath.
                                    handleClickOutside={() => undefined}
                                    handleClose={() => {
                                        closeModal(modal.id);
                                        modal.wrapperOptions?.handleCloseCallback && modal.wrapperOptions.handleCloseCallback();
                                    }}
                                    {...modal.modalOptions}>
                                    {modal.render()}
                                </DefaultModal>
                            </ActiveModalContext.Provider>
                        ))}
                    </>,
                    modalRootRef.current
                )}
        </ModalContext.Provider>
    );
};
